import {useRef, useState} from 'react';
import {ACCOUNT_ID} from '../api/account';
import {createTopup} from '../api/payment';
import {getYandexClientId, trackEvent} from '../utils/analytics';
import {showError} from '../utils/notify';
import {CLOUDPAYMENTS_PUBLIC_ID, PAYMENT_CURRENCY} from '../config/constants';

// Shared purchase plumbing for product paywalls: creates the top-up order and
// drives whichever embedded widget (YooKassa or CloudPayments) the backend
// picks. UI stays in the paywall components; only order/widget state lives here.
export function usePaywallPurchase({onSuccess, widgetContainerId = 'yookassa-widget-container'} = {}) {
    const [isPurchasing, setIsPurchasing] = useState(false);
    const [purchaseError, setPurchaseError] = useState(null);
    const [showYooKassaWidget, setShowYooKassaWidget] = useState(false);
    const yookassaWidgetRef = useRef(null);

    const openCloudPaymentsWidget = (orderId, amount, {planId, credits, description}) => {
        const widget = new window.cp.CloudPayments();
        widget.pay('charge', {
            publicId: CLOUDPAYMENTS_PUBLIC_ID,
            description,
            amount,
            currency: PAYMENT_CURRENCY,
            invoiceId: orderId,
            accountId: ACCOUNT_ID,
            paymentSchema: 'Single',
            data: {plan_id: planId, credits, order_id: orderId}
        }, {
            onSuccess: () => {
                onSuccess?.();
                setIsPurchasing(false);
            },
            onFail: () => {
                showError('Оплата не прошла. Попробуйте ещё раз.');
                setIsPurchasing(false);
            },
            onComplete: () => setIsPurchasing(false),
        });
    };

    const openYooKassaWidget = (confirmationToken) => {
        setShowYooKassaWidget(true);
        setTimeout(() => {
            if (yookassaWidgetRef.current) {
                const checkout = new window.YooMoneyCheckoutWidget({
                    confirmation_token: confirmationToken,
                    error_callback: (error) => {
                        console.error('YooKassa error:', error);
                        showError('Ошибка оплаты. Попробуйте ещё раз.');
                        setShowYooKassaWidget(false);
                        setIsPurchasing(false);
                    }
                });
                checkout.on('success', () => {
                    setShowYooKassaWidget(false);
                    onSuccess?.();
                    setIsPurchasing(false);
                });
                checkout.on('fail', () => {
                    showError('Оплата не прошла. Попробуйте ещё раз.');
                    setShowYooKassaWidget(false);
                    setIsPurchasing(false);
                });
                checkout.render(widgetContainerId);
            }
        }, 100);
    };

    const startPurchase = async ({planId, credits, description, analyticsEvent = 'start_topup', zvonokCall, videoGeneration}) => {
        trackEvent(analyticsEvent);
        setIsPurchasing(true);
        setPurchaseError(null);
        try {
            const yandexClientId = await getYandexClientId();
            const orderData = await createTopup({
                accountId: ACCOUNT_ID,
                planId,
                yandexClientId,
                zvonokCall,
                videoGeneration,
            });
            if (!orderData.purchase_id) throw new Error('No order ID');

            if (orderData.provider === 'yookassa' && orderData.confirmation_token) {
                openYooKassaWidget(orderData.confirmation_token);
            } else {
                openCloudPaymentsWidget(orderData.purchase_id, orderData.amount_rub, {planId, credits, description});
            }
        } catch (error) {
            console.error('Topup error:', error);
            setPurchaseError(error?.data?.detail || 'Ошибка создания заказа. Попробуйте ещё раз.');
            setIsPurchasing(false);
        }
    };

    const closeYooKassaWidget = () => {
        setShowYooKassaWidget(false);
        setIsPurchasing(false);
    };

    return {
        isPurchasing,
        purchaseError,
        showYooKassaWidget,
        yookassaWidgetRef,
        widgetContainerId,
        startPurchase,
        closeYooKassaWidget,
    };
}
